import { useEffect, useState } from 'react';
import api from '../../api/axios';

const CATEGORIES = ['Forms', 'Reports', 'Policies', 'Minutes', 'Other'];

export default function DocumentsManager() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('Other');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    api.get('/documents').then(r => setDocs(r.data || [])).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', title || file.name);
      fd.append('category', category);
      await api.post('/documents', fd);
      setTitle('');
      setFile(null);
      e.target.reset();
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    await api.delete(`/documents/${id}`);
    load();
  };

  const iconClass = (fileUrl = '') => {
    const ext = fileUrl.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return 'fa-file-pdf text-danger';
    if (['doc', 'docx'].includes(ext)) return 'fa-file-word text-primary';
    if (['xls', 'xlsx'].includes(ext)) return 'fa-file-excel text-success';
    return 'fa-file-alt text-secondary';
  };

  const filename = (url = '') => url.split('/').pop();
  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-AU') : '';

  return (
    <div>
      <div className="mb-4">
        <h4 className="fw-bold mb-0">Documents</h4>
        <p className="text-muted small mb-0">Upload downloadable files for the community</p>
      </div>

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body p-4">
          <h5 className="fw-bold mb-3">Upload Document</h5>
          {error && <div className="alert alert-danger py-2">{error}</div>}
          <form onSubmit={upload}>
            <div className="row g-3">
              <div className="col-md-5"><label className="form-label small">File *</label><input type="file" required onChange={e => setFile(e.target.files[0])} className="form-control" /></div>
              <div className="col-md-4"><label className="form-label small">Title</label><input type="text" value={title} onChange={e => setTitle(e.target.value)} className="form-control" placeholder="Display name (optional)" /></div>
              <div className="col-md-3">
                <label className="form-label small">Category</label>
                <select value={category} onChange={e => setCategory(e.target.value)} className="form-select">
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <button type="submit" disabled={uploading} className="btn btn-primary rounded-pill px-4 mt-3">
              {uploading ? 'Uploading...' : <><i className="fas fa-upload me-2"></i>Upload</>}
            </button>
          </form>
        </div>
      </div>

      {loading ? <div className="text-center py-5"><div className="spinner-border text-primary"></div></div> : (
        <div className="card border-0 shadow-sm">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light"><tr><th>File</th><th>Title</th><th>Category</th><th>Date</th><th></th></tr></thead>
              <tbody>
                {docs.length === 0 && <tr><td colSpan="5" className="text-center text-muted py-4">No documents uploaded yet.</td></tr>}
                {docs.map(d => (
                  <tr key={d.id}>
                    <td><i className={`fas ${iconClass(d.file_url)} me-2`}></i><small>{filename(d.file_url)}</small></td>
                    <td>{d.title}</td>
                    <td><span className="badge badge-soft-secondary">{d.category}</span></td>
                    <td><small>{fmtDate(d.created_at)}</small></td>
                    <td>
                      <a href={d.file_url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary me-2">View</a>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => remove(d.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
